
import React from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../components/Button';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-b from-indigo-50 to-white pt-16 pb-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="lg:grid lg:grid-cols-12 lg:gap-8 items-center">
            <div className="sm:text-center md:max-w-2xl md:mx-auto lg:col-span-6 lg:text-left">
              <div className="inline-flex items-center px-4 py-2 rounded-full border border-indigo-100 bg-white shadow-sm mb-6">
                <span className="px-3 py-1 text-xs font-bold leading-tight text-brand-blue bg-indigo-100 rounded-full">NEW</span>
                <span className="ml-3 text-sm font-medium text-slate-500">Parent Dashboard now available! 👨‍👩‍👧‍👦</span>
              </div>
              <h1 className="text-4xl tracking-tight font-extrabold text-slate-900 sm:text-5xl md:text-6xl lg:text-5xl xl:text-6xl brand-font">
                <span className="block">Master your grades</span>
                <span className="block text-brand-blue">with AI superpowers.</span>
              </h1>
              <p className="mt-3 text-base text-slate-500 sm:mt-5 sm:text-lg sm:max-w-xl sm:mx-auto md:mt-5 md:text-xl lg:mx-0">
                CleverCubby creates infinite, personalized quizzes aligned to your State's curriculum. From Texas TEKS to Common Core, we make learning Math, Science, and more feel like a game.
              </p>
              <div className="mt-8 sm:max-w-lg sm:mx-auto sm:text-center lg:text-left lg:mx-0">
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button size="lg" onClick={() => navigate('/register')} className="w-full sm:w-auto justify-center shadow-xl shadow-indigo-200">
                    Join for Free
                  </Button>
                  <Button variant="outline" size="lg" onClick={() => navigate('/login')} className="w-full sm:w-auto justify-center">
                    Student Login
                  </Button>
                </div>
                <p className="mt-3 text-xs text-slate-400">No credit card required • K-12th Grade • COPPA Compliant</p>
              </div>
            </div>

            {/* Hero Visual */}
            <div className="mt-12 relative sm:max-w-lg sm:mx-auto lg:mt-0 lg:max-w-none lg:mx-0 lg:col-span-6 lg:flex lg:items-center">
              <div className="relative mx-auto w-full rounded-3xl shadow-2xl lg:max-w-md overflow-hidden bg-white border-4 border-white transform rotate-2 hover:rotate-0 transition-transform duration-500">
                <div className="bg-brand-blue p-6 text-white text-center">
                  <div className="text-5xl mb-2">🏆</div>
                  <h3 className="text-2xl font-bold brand-font">Math Whiz Quiz</h3>
                  <p className="opacity-80">Grade 4 • Texas (TEKS)</p>
                </div>
                <div className="p-6 space-y-4">
                  <div className="h-4 bg-slate-100 rounded-full w-3/4"></div>
                  <div className="h-4 bg-slate-100 rounded-full w-1/2"></div>
                  <div className="p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3 text-green-800 font-bold">
                    <i className="fas fa-check-circle text-xl"></i> Correct Answer!
                  </div>
                  <div className="p-4 bg-slate-50 rounded-xl border border-slate-100 opacity-50">
                    Wrong Answer
                  </div>
                </div>
              </div>

              {/* Floating Elements */}
              <div className="absolute -top-12 -right-12 text-9xl text-yellow-400 opacity-20 animate-bounce delay-700">
                <i className="fas fa-star"></i>
              </div>
              <div className="absolute -bottom-12 -left-12 text-9xl text-brand-blue opacity-10 animate-pulse">
                <i className="fas fa-shapes"></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Strip */}
      <div className="bg-brand-blue">
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 gap-8 md:grid-cols-4 text-center">
            {[
              { label: 'States Supported', val: '50' },
              { label: 'Grade Levels', val: 'K-12' },
              { label: 'Questions Generated', val: '∞' },
              { label: 'Subjects', val: '4+' }
            ].map((stat, i) => (
              <div key={i} className="flex flex-col">
                <dt className="order-2 mt-2 text-sm font-medium leading-6 text-indigo-200 uppercase tracking-wider">{stat.label}</dt>
                <dd className="order-1 text-4xl font-extrabold text-white">{stat.val}</dd>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Features Grid */}
      <div className="py-24 bg-white relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-base text-brand-blue font-semibold tracking-wide uppercase">Why CleverCubby?</h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-slate-900 sm:text-4xl brand-font">
              Customized learning for every student.
            </p>
            <p className="mt-4 max-w-2xl text-xl text-slate-500 lg:mx-auto">
              Stop practicing generic questions. Get content that actually matches what your child is learning in school right now.
            </p>
          </div>

          <div className="mt-20">
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {[
                { icon: 'fa-chalkboard-teacher', title: 'Teacher Dashboard', desc: 'Create classes, assign AI-generated homework, and track student mastery in real-time. The perfect classroom companion.', color: 'bg-teal-100 text-teal-600' },
                { icon: 'fa-university', title: 'Standardized Test Center', desc: 'Full-length practice exams for STAAR, CAASPP, FAST, and AP Tests. Experience real test conditions before the big day.', color: 'bg-indigo-100 text-indigo-600' },
                { icon: 'fa-robot', title: 'AI-Powered Tutor', desc: 'Our AI engine generates unique questions every time, adapting to your child\'s skill level instantly with Google Gemini technology.', color: 'bg-blue-100 text-blue-600' },
                { icon: 'fa-map-marked-alt', title: '50-State & AP Analysis', desc: 'Curriculum aligned to TEKS, Common Core, and Advanced Placement standards. We cover K-12 through College Prep.', color: 'bg-green-100 text-green-600' },
                { icon: 'fa-users', title: 'Parent Dashboard', desc: 'Manage multiple children, track their progress, add new students, and monitor quiz history - all from one place.', color: 'bg-purple-100 text-purple-600' },
                { icon: 'fa-brain', title: 'Adaptive Learning', desc: 'AI analyzes performance and recommends personalized topics and difficulty levels to maximize learning efficiency.', color: 'bg-pink-100 text-pink-600' },
                { icon: 'fa-gamepad', title: 'Gamified Fun', desc: 'Earn points, unlock badges, and climb the leaderboard. Homework doesn\'t have to be boring!', color: 'bg-yellow-100 text-yellow-600' },
                { icon: 'fa-layer-group', title: 'Flashcards', desc: 'Need to memorize vocabulary or math facts? Generate custom flashcard decks in seconds with AI.', color: 'bg-cyan-100 text-cyan-600' },
                { icon: 'fa-chart-line', title: 'Progress Tracking', desc: 'Visualize subject mastery with detailed analytics. See exactly where your child excels and needs help.', color: 'bg-red-100 text-red-600' },
              ].map((feature, i) => (
                <div key={i} className="pt-6">
                  <div className="flow-root bg-slate-50 rounded-3xl px-6 pb-8 h-full hover:shadow-xl transition-shadow duration-300 border border-slate-100">
                    <div className="-mt-6">
                      <div>
                        <span className={`inline-flex items-center justify-center p-3 rounded-2xl shadow-lg ${feature.color}`}>
                          <i className={`fas ${feature.icon} text-2xl`}></i>
                        </span>
                      </div>
                      <h3 className="mt-8 text-lg font-bold text-slate-900 tracking-tight">{feature.title}</h3>
                      <p className="mt-5 text-base text-slate-500">{feature.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* How it Works Step */}
      <div className="bg-brand-light py-24 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-extrabold text-indigo-900 brand-font">How it Works</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8 text-center relative">
            {/* Connector Line (Desktop) */}
            <div className="hidden md:block absolute top-12 left-1/6 right-1/6 h-1 bg-indigo-200 -z-10"></div>

            {[
              { step: '1', title: 'Create Profile', desc: 'Sign up and select your Grade and State.' },
              { step: '2', title: 'Pick a Topic', desc: 'Type in what you are learning (e.g. "Planets").' },
              { step: '3', title: 'Start Learning', desc: 'Take quizzes, earn points, and master the subject!' },
            ].map((s, i) => (
              <div key={i} className="bg-white p-8 rounded-3xl shadow-lg relative">
                <div className="w-16 h-16 bg-brand-blue text-white text-2xl font-bold rounded-full flex items-center justify-center mx-auto mb-6 border-4 border-indigo-100">
                  {s.step}
                </div>
                <h3 className="text-xl font-bold text-slate-800 mb-2">{s.title}</h3>
                <p className="text-slate-500">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-white">
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:py-16 lg:px-8 lg:flex lg:items-center lg:justify-between">
          <h2 className="text-3xl font-extrabold tracking-tight text-gray-900 sm:text-4xl brand-font">
            <span className="block">Ready to boost those grades?</span>
            <span className="block text-brand-blue">Start your free trial today.</span>
          </h2>
          <div className="mt-8 flex lg:mt-0 lg:flex-shrink-0 gap-3">
            <div className="inline-flex rounded-md shadow">
              <Button size="lg" onClick={() => navigate('/register')}>
                Get Started
              </Button>
            </div>
            <div className="inline-flex rounded-md shadow">
              <Button variant="outline" size="lg" onClick={() => navigate('/login')}>
                Log In
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Footer / Privacy Link */}
      <div className="bg-slate-50 border-t border-slate-200 py-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-slate-400 text-sm mb-2">
            <a href="#" className="hover:text-brand-blue">Privacy Policy</a> •
            <a href="#" className="hover:text-brand-blue ml-4">Terms of Service</a> •
            <a href="#" className="hover:text-brand-blue ml-4">Contact Support</a>
          </p>
          <p className="text-slate-400 text-sm">&copy; 2024 CleverCubby Learning. Safe for Kids.</p>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
